import { useState } from 'react';
import styled from '@emotion/styled';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Badge, badgeStateToColor, evaluateKpi, getScoreColor, KPI_METRIC_TYPE, theme } from '@arnold/common';
import { QuestionType } from '../../../../generated/hooks';
import { isMultipleChoice, type CustomMetricSummary } from '@arnold/core';
import { inferQuestionScoring } from '../../questionScoring';
import type { ReportQuestion } from '../../types';
import { MetricValue } from '../MetricValue';
import { formatMetricNumber } from '../formatMetricNumber';
import { BandDistribution } from '../BandDistribution';
import {
  ATTRITION_RISK_PRESET,
  BAND_FLOOR,
  ENGAGEMENT_BANDS,
  localizedText,
  METRIC_IDENTIFIER_MAX_LENGTH,
  newDefinitionId,
  type MetricRenderer,
  type ReportMetricDefinition,
  type RespondentCondition,
  type ScaleBand,
  type ScaleSource,
  type ScoreSource,
  type MetricCalculation,
  type Normalization,
} from '../types';
import { RespondentSegmentEditor } from './RespondentSegmentEditor';
import { emptySource, makeNpsSource, makeScaleSource, makeScoreSource } from './sourceFactories';
import type { ComponentWidth } from './useOverviewEditor';
import {
  Action,
  Bar,
  Check,
  Details,
  ErrorText,
  Field,
  FormGrid,
  FormSection,
  Hint,
  InlineAdd,
  MappingRow,
  SourceBlock,
} from './styles';

/** What the picker offers. "One question" and "several questions" are both the `average` calculation. */
type Picker =
  | 'averageOne'
  | 'nps'
  | 'choiceMatchRate'
  | 'averageMany'
  | 'esi'
  | 'compositeAverage'
  | 'scaleTotal'
  | 'scaleBands'
  | 'respondentSegment';
const PICKERS: Picker[] = [
  'averageOne',
  'nps',
  'choiceMatchRate',
  'averageMany',
  'esi',
  'compositeAverage',
  'scaleTotal',
  'scaleBands',
  'respondentSegment',
];
type ChoiceSource = { questionIndex: string; statementIndex?: number; optionIndexes: number[] };
type ScaleCalculation = Extract<MetricCalculation, { type: 'scaleTotal' | 'scaleBands' }>;

/** The engagement preset: engaged / neutral / disengaged with the standard thresholds, labelled in `language`. */
const engagementBands = (t: TFunction, language: string): ScaleBand[] =>
  ENGAGEMENT_BANDS.map((band) => ({ id: band.id, from: band.from, label: { [language]: t(band.labelKey) } }));

/** The attrition-risk preset: two categories one deviation below the mean, plus eNPS detractors when available. */
const attritionRiskConditions = (
  t: TFunction,
  language: string,
  npsQuestion: ReportQuestion | undefined,
): RespondentCondition[] => [
  ...ATTRITION_RISK_PRESET.categories.map(
    (category): RespondentCondition => ({
      kind: 'belowDeviation',
      id: category.id,
      label: { [language]: t(category.labelKey) },
      sources: [],
      deviations: ATTRITION_RISK_PRESET.deviations,
    }),
  ),
  {
    kind: 'npsBucket',
    id: ATTRITION_RISK_PRESET.npsConditionId,
    ...(npsQuestion ? makeNpsSource(npsQuestion) : { source: { ...emptySource(), max: 10 }, inputMin: 0 as const }),
    buckets: [...ATTRITION_RISK_PRESET.buckets],
  },
];
function withMappedRange(source: ScoreSource): ScoreSource {
  const values = source.optionScores.filter((value): value is number => value !== null && Number.isFinite(value));
  return source.rangeMode === 'mapped'
    ? { ...source, min: values.length ? Math.min(...values) : 0, max: values.length ? Math.max(...values) : 1 }
    : source;
}
/** Questions on different scales are normalized automatically; nobody should have to know what that means. */
const autoNormalization = (sources: ScoreSource[]): Normalization =>
  new Set(sources.filter((source) => source.questionIndex).map((source) => `${source.min}:${source.max}`)).size > 1
    ? 'range'
    : 'none';
/** Typed codes are normalized as entered so only valid identifiers ever reach the draft. */
function withIdentifier(metric: ReportMetricDefinition, value: string): ReportMetricDefinition {
  const identifier = value
    .toUpperCase()
    .replace(/[^A-Z0-9_]/g, '')
    .slice(0, METRIC_IDENTIFIER_MAX_LENGTH);
  const next: ReportMetricDefinition = { ...metric, identifier };
  if (!identifier) delete next.identifier;
  return next;
}
const sameScores = (a: (number | null)[], b: (number | null)[] | undefined) =>
  !!b && a.length === b.length && a.every((value, index) => value === b[index]);

export function makeMetric(
  question: ReportQuestion | undefined,
  language: string,
  title: string,
): ReportMetricDefinition {
  const calculation: MetricCalculation = question
    ? { type: 'scaleTotal', sources: [makeScaleSource(question)], normalization: 'none' }
    : {
        type: 'average',
        sources: [emptySource()],
        normalization: 'none',
      };
  return {
    id: newDefinitionId(),
    title: { [language]: title },
    description: {},
    calculation,
    presentation: { renderer: 'bar', format: 'outOfMax' },
  };
}

export function MetricEditor({
  metric,
  metrics,
  questions,
  language,
  onChange,
  error,
  summary,
  width,
  onWidthChange,
}: {
  metric: ReportMetricDefinition;
  metrics: ReportMetricDefinition[];
  questions: ReportQuestion[];
  language: string;
  onChange: (metric: ReportMetricDefinition) => void;
  error?: string;
  summary?: CustomMetricSummary;
  /** Overview width of the placed metric card; undefined for a metric that is not in the overview. */
  width?: ComponentWidth;
  onWidthChange?: (width: ComponentWidth) => void;
}) {
  const { t, i18n } = useTranslation('newReport');
  const { t: tKpiCard } = useTranslation('kpiCard');
  const calculation = metric.calculation;
  const [many, setMany] = useState(calculation.type === 'average' && calculation.sources.length > 1);
  const picker: Picker = calculation.type === 'average' ? (many ? 'averageMany' : 'averageOne') : calculation.type;
  const choiceQuestions = questions.filter((question) =>
    [QuestionType.Select, QuestionType.Scale, QuestionType.Multiselect].includes(question.type),
  );
  const scorable = choiceQuestions.filter((question) => !isMultipleChoice(question));
  const scaleQuestions = questions.filter((question) => question.type === QuestionType.Scale);
  const scaleBased = calculation.type === 'scaleTotal' || calculation.type === 'scaleBands';
  const candidates = calculation.type === 'choiceMatchRate' ? choiceQuestions : scaleBased ? scaleQuestions : scorable;
  /** Scale metrics whose questions a scale total or banded metric can merge into its own. */
  const scaleTotalMetrics = metrics.filter((item) => item.id !== metric.id && item.calculation.type === 'scaleTotal');
  const setScaleCalculation = (patch: Partial<Pick<ScaleCalculation, 'sources' | 'metricIds'>>) => {
    if (calculation.type === 'scaleTotal' || calculation.type === 'scaleBands')
      setCalculation({ ...calculation, ...patch } as MetricCalculation);
  };
  const setCalculation = (value: MetricCalculation) => onChange({ ...metric, calculation: value });
  const setAverageSources = (sources: ScoreSource[]) =>
    setCalculation({ type: 'average', sources, normalization: autoNormalization(sources) });
  const setEsiSources = (sources: ScoreSource[]) =>
    setCalculation({ type: 'esi', sources, normalization: autoNormalization(sources) });
  const changeType = (type: MetricCalculation['type']) => {
    let next: MetricCalculation;
    if (type === 'nps') {
      const question = scorable.find((item) => inferQuestionScoring(item).nps);
      next = {
        type,
        source: question ? makeScoreSource(question) : { ...emptySource(), max: 10 },
        inputMin: question ? inferQuestionScoring(question).nps?.minValue ?? 0 : 0,
      };
    } else if (type === 'compositeAverage') next = { type, metricIds: [], normalization: 'none' };
    else if (type === 'esi') next = { type, sources: [emptySource()], normalization: 'none' };
    else if (type === 'scaleTotal') next = { type, sources: [{ questionIndex: '' }], normalization: 'none' };
    else if (type === 'scaleBands')
      next = {
        type,
        sources: [{ questionIndex: '' }],
        bands: engagementBands(t, language),
        primaryBandId: ENGAGEMENT_BANDS[0].id,
      };
    else if (type === 'choiceMatchRate')
      next = { type, sources: [{ questionIndex: '', optionIndexes: [] }], complement: false };
    else if (type === 'respondentSegment')
      next = {
        type,
        conditions: attritionRiskConditions(
          t,
          language,
          scorable.find((item) => inferQuestionScoring(item).nps),
        ),
        labels: {
          matching: { [language]: t(ATTRITION_RISK_PRESET.labels.matching) },
          other: { [language]: t(ATTRITION_RISK_PRESET.labels.other) },
        },
      };
    else next = { type, sources: [emptySource()], normalization: 'none' };
    const banded = type === 'scaleBands' || type === 'respondentSegment';
    onChange({
      ...metric,
      calculation: next,
      presentation: {
        renderer: banded ? 'distribution' : 'bar',
        format:
          type === 'nps' || type === 'esi'
            ? 'number'
            : type === 'choiceMatchRate' || banded
              ? 'maxPercent'
              : 'outOfMax',
      },
    });
  };
  const changePicker = (next: Picker) => {
    if (next === 'averageOne' || next === 'averageMany') {
      setMany(next === 'averageMany');
      if (calculation.type !== 'average') changeType('average');
      else if (next === 'averageOne' && calculation.sources.length > 1)
        setAverageSources(calculation.sources.slice(0, 1));
    } else changeType(next);
  };
  const sources: (ScoreSource | ScaleSource | ChoiceSource)[] =
    calculation.type === 'nps'
      ? [calculation.source]
      : calculation.type === 'compositeAverage' || calculation.type === 'respondentSegment'
        ? []
        : calculation.sources;
  const updateSource = (index: number, source: ScoreSource | ScaleSource | ChoiceSource) => {
    if (calculation.type === 'nps') setCalculation({ ...calculation, source: source as ScoreSource });
    else if (calculation.type === 'average' || calculation.type === 'esi')
      (calculation.type === 'average' ? setAverageSources : setEsiSources)(
        calculation.sources.map((item, i) => (i === index ? (source as ScoreSource) : item)),
      );
    else if (calculation.type === 'scaleTotal' || calculation.type === 'scaleBands')
      setScaleCalculation({
        sources: calculation.sources.map((item, i) => (i === index ? (source as ScaleSource) : item)),
      });
    else if (calculation.type === 'choiceMatchRate')
      setCalculation({
        ...calculation,
        sources: calculation.sources.map((item, i) => (i === index ? (source as ChoiceSource) : item)),
      });
  };
  const removeSource = (index: number) => {
    if (calculation.type === 'average' || calculation.type === 'esi')
      (calculation.type === 'average' ? setAverageSources : setEsiSources)(
        calculation.sources.filter((_, i) => i !== index),
      );
    if (calculation.type === 'scaleTotal' || calculation.type === 'scaleBands')
      setScaleCalculation({ sources: calculation.sources.filter((_, i) => i !== index) });
    if (calculation.type === 'choiceMatchRate')
      setCalculation({ ...calculation, sources: calculation.sources.filter((_, i) => i !== index) });
  };
  const addSource = () => {
    if (calculation.type === 'average' || calculation.type === 'esi')
      (calculation.type === 'average' ? setAverageSources : setEsiSources)([...calculation.sources, emptySource()]);
    if (calculation.type === 'scaleTotal' || calculation.type === 'scaleBands')
      setScaleCalculation({ sources: [...calculation.sources, { questionIndex: '' }] });
    if (calculation.type === 'choiceMatchRate')
      setCalculation({ ...calculation, sources: [...calculation.sources, { questionIndex: '', optionIndexes: [] }] });
  };
  const canAddSource =
    calculation.type === 'choiceMatchRate' ||
    scaleBased ||
    calculation.type === 'esi' ||
    (calculation.type === 'average' && many);
  const canRemoveSource =
    sources.length > 1 ||
    ((calculation.type === 'scaleTotal' || calculation.type === 'scaleBands') && !!calculation.metricIds?.length);
  const bands = calculation.type === 'scaleBands' ? calculation.bands : [];
  const updateBands = (next: ScaleBand[], primaryBandId?: string) => {
    if (calculation.type !== 'scaleBands') return;
    const primary = primaryBandId ?? calculation.primaryBandId;
    setCalculation({
      ...calculation,
      bands: next,
      primaryBandId: next.some((band) => band.id === primary) ? primary : next[0]?.id ?? '',
    });
  };
  /** A new band is inserted above the floor band, halfway between its neighbours. */
  const addBand = () => {
    const above = bands[bands.length - 2];
    const floor = bands[bands.length - 1];
    const from = above && floor ? (above.from + floor.from) / 2 : BAND_FLOOR;
    const band: ScaleBand = { id: newDefinitionId(), label: { [language]: '' }, from };
    updateBands([...bands.slice(0, -1), band, ...bands.slice(-1)]);
  };
  /** The breakdown view is offered for metrics composed of other metrics or of several questions. */
  const composed =
    calculation.type === 'compositeAverage'
      ? calculation.metricIds.length > 0
      : (calculation.type === 'average' || calculation.type === 'esi') && calculation.sources.length > 1;
  return (
    <div>
      <FormSection>
        <h3>{t('definition.metricNameStep')}</h3>
        <Field>
          {t('definition.title')} ({language})
          <input
            value={metric.title[language] ?? ''}
            onChange={(event) => onChange({ ...metric, title: { ...metric.title, [language]: event.target.value } })}
          />
        </Field>
        <Field>
          {t('definition.description')} ({language})
          <textarea
            value={metric.description[language] ?? ''}
            onChange={(event) =>
              onChange({ ...metric, description: { ...metric.description, [language]: event.target.value } })
            }
          />
        </Field>
        <Field>
          {t('definition.identifier')}
          <input
            value={metric.identifier ?? ''}
            maxLength={METRIC_IDENTIFIER_MAX_LENGTH}
            placeholder={t('definition.identifierPlaceholder')}
            onChange={(event) => onChange(withIdentifier(metric, event.target.value))}
          />
        </Field>
        <Hint>{t('definition.identifierHelp')}</Hint>
      </FormSection>
      <FormSection>
        <h3>{t('definition.metricCalculationStep')}</h3>
        <Field>
          {t('definition.calculation')}
          <select value={picker} onChange={(event) => changePicker(event.target.value as Picker)}>
            {PICKERS.map((value) => (
              <option key={value} value={value}>
                {t(`definition.calculations.${value}`)}
              </option>
            ))}
          </select>
        </Field>
        <Hint>{t(`definition.calculationHelp.${picker}`)}</Hint>
      </FormSection>
      <FormSection>
        <h3>{t('definition.metricSourcesStep')}</h3>
        {calculation.type === 'compositeAverage' && (
          <>
            {metrics
              .filter((item) => item.id !== metric.id)
              .map((item) => (
                <Check key={item.id}>
                  <input
                    type="checkbox"
                    checked={calculation.metricIds.includes(item.id)}
                    onChange={(event) =>
                      setCalculation({
                        ...calculation,
                        metricIds: event.target.checked
                          ? [...calculation.metricIds, item.id]
                          : calculation.metricIds.filter((id) => id !== item.id),
                      })
                    }
                  />
                  {localizedText(item.title, language) || t('definition.newMetric')}
                </Check>
              ))}
            {metrics.length < 2 && <Hint>{t('definition.compositeNeedsMetrics')}</Hint>}
            <Check>
              <input
                type="checkbox"
                checked={calculation.normalization === 'range'}
                onChange={(event) =>
                  setCalculation({ ...calculation, normalization: event.target.checked ? 'range' : 'none' })
                }
              />
              {t('definition.differentScales')}
            </Check>
          </>
        )}
        {calculation.type === 'scaleBands' && scaleTotalMetrics.length > 0 && (
          <>
            <Hint>{t('definition.calculationHelp.scaleTotal')}</Hint>
            {scaleTotalMetrics.map((item) => (
              <Check key={item.id}>
                <input
                  type="checkbox"
                  checked={calculation.metricIds?.includes(item.id) ?? false}
                  onChange={(event) => {
                    const metricIds = event.target.checked
                      ? [...(calculation.metricIds ?? []), item.id]
                      : (calculation.metricIds ?? []).filter((id) => id !== item.id);
                    const sources = calculation.sources.filter((source) => source.questionIndex);
                    const nextSources =
                      sources.length || event.target.checked
                        ? sources
                        : candidates[0]
                          ? [makeScaleSource(candidates[0])]
                          : [];
                    setScaleCalculation({
                      metricIds,
                      sources: nextSources,
                    });
                  }}
                />
                {localizedText(item.title, language) || t('definition.newMetric')}
              </Check>
            ))}
          </>
        )}
        {calculation.type === 'respondentSegment' && (
          <RespondentSegmentEditor
            calculation={calculation}
            metrics={scaleTotalMetrics}
            questions={questions}
            language={language}
            summary={summary}
            onChange={setCalculation}
          />
        )}
        {calculation.type === 'choiceMatchRate' && (
          <Check>
            <input
              type="checkbox"
              checked={calculation.complement}
              onChange={(event) => setCalculation({ ...calculation, complement: event.target.checked })}
            />
            {t('definition.complement')}
          </Check>
        )}
        {sources.map((source, index) => {
          const question = questions.find((item) => item.index === source.questionIndex);
          const inferred = question ? inferQuestionScoring(question) : {};
          const score = 'min' in source ? source : undefined;
          const scored = !!score && !scaleBased;
          const detected =
            scored &&
            (calculation.type === 'nps'
              ? sameScores(score!.optionScores, inferred.nps?.optionScores)
              : score!.rangeMode !== 'custom' && sameScores(score!.optionScores, inferred.average?.optionScores));
          const scaleMapped = calculation.type === 'scaleTotal' || calculation.type === 'scaleBands';
          const scaleSource = scaleMapped ? (source as ScaleSource) : undefined;
          const scaleScores: (number | null)[] =
            scaleSource?.optionScores && scaleSource.optionScores.length === question?.answersLocal?.length
              ? scaleSource.optionScores
              : question
                ? inferQuestionScoring(question).average?.optionScores ?? (question.answersLocal ?? []).map(() => null)
                : [];
          return (
            <SourceBlock key={index}>
              <InlineAdd>
                <Field>
                  {t('definition.question')} {sources.length > 1 ? index + 1 : ''}
                  <select
                    value={source.questionIndex}
                    onChange={(event) => {
                      const nextQuestion = candidates.find((item) => item.index === event.target.value);
                      if (!nextQuestion) return;
                      if (calculation.type === 'nps')
                        setCalculation({
                          ...calculation,
                          source: makeScoreSource(nextQuestion),
                          inputMin:
                            inferQuestionScoring(nextQuestion).nps?.minValue ??
                            (nextQuestion.answersLocal?.length === 10 ? 1 : 0),
                        });
                      else if (scaleBased) updateSource(index, makeScaleSource(nextQuestion));
                      else
                        updateSource(
                          index,
                          calculation.type === 'choiceMatchRate'
                            ? { questionIndex: nextQuestion.index, optionIndexes: [] }
                            : makeScoreSource(nextQuestion),
                        );
                    }}
                  >
                    <option value="">{t('definition.chooseQuestion')}</option>
                    {candidates
                      .filter(
                        (item) =>
                          (calculation.type === 'nps' && item.type === QuestionType.Select) ||
                          (calculation.type === 'scaleTotal' && item.type === QuestionType.Scale) ||
                          (calculation.type !== 'nps' && calculation.type !== 'scaleTotal'),
                      )
                      .map((item) => (
                        <option key={item.index} value={item.index}>
                          {item.text}
                        </option>
                      ))}
                  </select>
                </Field>
                {canRemoveSource && (
                  <Action title={t('definition.removeQuestion')} onClick={() => removeSource(index)}>
                    ×
                  </Action>
                )}
              </InlineAdd>
              {!!question?.statementsLocal?.length && (
                <Field>
                  {t('tableTest.statement')}
                  <select
                    value={source.statementIndex ?? ''}
                    onChange={(event) =>
                      updateSource(index, {
                        ...source,
                        statementIndex: event.target.value === '' ? undefined : Number(event.target.value),
                      })
                    }
                  >
                    <option value="">{t('tableTest.wholeQuestion')}</option>
                    {question.statementsLocal.map((label, i) => (
                      <option key={i} value={i}>
                        {label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}
              {question && !scored && calculation.type === 'choiceMatchRate' && (
                <>
                  <Hint>{t('definition.matchingOptionsHelp')}</Hint>
                  <div>
                    {(question.answersLocal ?? []).map((label, option) => (
                      <MappingRow key={option}>
                        <span>{label}</span>
                        <input
                          type="checkbox"
                          checked={(source as ChoiceSource).optionIndexes.includes(option)}
                          onChange={(event) =>
                            updateSource(index, {
                              ...source,
                              optionIndexes: event.target.checked
                                ? [...(source as ChoiceSource).optionIndexes, option]
                                : (source as ChoiceSource).optionIndexes.filter((i) => i !== option),
                            })
                          }
                        />
                      </MappingRow>
                    ))}
                  </div>
                </>
              )}
              {question && scaleMapped && (
                <Details open>
                  <summary>{t('definition.assignValues')}</summary>
                  <div>
                    <Hint>{t('definition.mappingHelp')}</Hint>
                    {(question.answersLocal ?? []).map((label, option) => (
                      <MappingRow key={option}>
                        <span>{label}</span>
                        <input
                          type="number"
                          step="any"
                          value={scaleScores[option] ?? ''}
                          onChange={(event) =>
                            updateSource(index, {
                              ...source,
                              optionScores: scaleScores.map((value, i) =>
                                i === option ? (event.target.value === '' ? null : Number(event.target.value)) : value,
                              ),
                            })
                          }
                        />
                      </MappingRow>
                    ))}
                  </div>
                </Details>
              )}
              {question && scored && (
                <Details key={`${source.questionIndex}:${detected}`} open={!detected}>
                  <summary>
                    {detected
                      ? t('definition.valuesDetected', { min: score!.min, max: score!.max })
                      : t('definition.assignValues')}
                  </summary>
                  <div>
                    {calculation.type === 'nps' && (
                      <>
                        <Field>
                          {t('definition.npsRange')}
                          <select
                            value={calculation.inputMin}
                            onChange={(event) =>
                              setCalculation({ ...calculation, inputMin: Number(event.target.value) as 0 | 1 })
                            }
                          >
                            <option value={0}>0–10</option>
                            <option value={1}>1–10</option>
                          </select>
                        </Field>
                        <Bar>
                          {(['up', 'down'] as const).map((direction) => (
                            <Action
                              key={direction}
                              onClick={() => {
                                const length = 11 - calculation.inputMin;
                                setCalculation({
                                  ...calculation,
                                  source: {
                                    ...calculation.source,
                                    min: calculation.inputMin,
                                    max: 10,
                                    optionScores: (question.answersLocal ?? []).map((_, i) =>
                                      i >= length ? null : direction === 'up' ? i + calculation.inputMin : 10 - i,
                                    ),
                                  },
                                });
                              }}
                            >
                              {t(direction === 'up' ? 'tableTest.npsUp' : 'tableTest.npsDown', {
                                min: calculation.inputMin,
                              })}
                            </Action>
                          ))}
                        </Bar>
                      </>
                    )}
                    <Hint>{t('definition.mappingHelp')}</Hint>
                    <div>
                      {(question.answersLocal ?? []).map((label, option) => (
                        <MappingRow key={option}>
                          <span>{label}</span>
                          <input
                            type="number"
                            step="any"
                            value={score!.optionScores[option] ?? ''}
                            onChange={(event) => {
                              const next = {
                                ...score!,
                                optionScores: score!.optionScores.map((value, i) =>
                                  i === option
                                    ? event.target.value === ''
                                      ? null
                                      : Number(event.target.value)
                                    : value,
                                ),
                              };
                              updateSource(
                                index,
                                calculation.type === 'nps'
                                  ? { ...next, min: calculation.inputMin, max: 10 }
                                  : withMappedRange(next),
                              );
                            }}
                          />
                        </MappingRow>
                      ))}
                    </div>
                    {calculation.type !== 'nps' && (
                      <Details open={score!.rangeMode === 'custom'}>
                        <summary>{t('definition.advancedBounds')}</summary>
                        <div>
                          <Check>
                            <input
                              type="checkbox"
                              checked={score!.rangeMode === 'custom'}
                              onChange={(event) =>
                                updateSource(
                                  index,
                                  withMappedRange({ ...score!, rangeMode: event.target.checked ? 'custom' : 'mapped' }),
                                )
                              }
                            />
                            {t('definition.customBounds')}
                          </Check>
                          {score!.rangeMode === 'custom' ? (
                            <FormGrid>
                              <Field>
                                {t('definition.minimum')}
                                <input
                                  type="number"
                                  step="any"
                                  value={score!.min}
                                  onChange={(event) =>
                                    updateSource(index, { ...score!, min: Number(event.target.value) })
                                  }
                                />
                              </Field>
                              <Field>
                                {t('definition.maximum')}
                                <input
                                  type="number"
                                  step="any"
                                  value={score!.max}
                                  onChange={(event) =>
                                    updateSource(index, { ...score!, max: Number(event.target.value) })
                                  }
                                />
                              </Field>
                            </FormGrid>
                          ) : (
                            <Hint>{t('definition.detectedRange', { min: score!.min, max: score!.max })}</Hint>
                          )}
                        </div>
                      </Details>
                    )}
                  </div>
                </Details>
              )}
            </SourceBlock>
          );
        })}
        {(calculation.type === 'average' || calculation.type === 'esi') && calculation.normalization === 'range' && (
          <Hint>{t('definition.autoNormalized')}</Hint>
        )}
        {canAddSource && (
          <Bar>
            <Action onClick={addSource}>+ {t('definition.addAnotherQuestion')}</Action>
          </Bar>
        )}
      </FormSection>
      {calculation.type === 'scaleBands' && (
        <FormSection>
          <h3>{t('definition.metricBandsStep')}</h3>
          <Hint>{t('definition.bandsHelp')}</Hint>
          {bands.map((band, index) => {
            const last = index === bands.length - 1;
            return (
              <SourceBlock key={band.id}>
                <InlineAdd>
                  <Field>
                    {t('definition.bandLabel')} ({language})
                    <input
                      value={band.label[language] ?? ''}
                      onChange={(event) =>
                        updateBands(
                          bands.map((item, i) =>
                            i === index ? { ...item, label: { ...item.label, [language]: event.target.value } } : item,
                          ),
                        )
                      }
                    />
                  </Field>
                  <Field>
                    {t('definition.bandFrom')}
                    <input
                      type="number"
                      step="any"
                      disabled={last}
                      value={last ? '' : band.from}
                      placeholder={last ? t('definition.bandFloor') : undefined}
                      onChange={(event) =>
                        updateBands(
                          bands.map((item, i) => (i === index ? { ...item, from: Number(event.target.value) } : item)),
                        )
                      }
                    />
                  </Field>
                  {bands.length > 2 && !last && (
                    <Action
                      title={t('definition.removeBand')}
                      onClick={() => updateBands(bands.filter((_, i) => i !== index))}
                    >
                      ×
                    </Action>
                  )}
                </InlineAdd>
              </SourceBlock>
            );
          })}
          {bands.length < 10 && (
            <Bar>
              <Action onClick={addBand}>+ {t('definition.addBand')}</Action>
            </Bar>
          )}
          <Field>
            {t('definition.primaryBand')}
            <select value={calculation.primaryBandId} onChange={(event) => updateBands(bands, event.target.value)}>
              {bands.map((band) => (
                <option key={band.id} value={band.id}>
                  {band.label[language] || localizedText(band.label, language) || t('definition.unnamedBand')}
                </option>
              ))}
            </select>
          </Field>
          <Hint>{t('definition.primaryBandHelp')}</Hint>
        </FormSection>
      )}
      <FormSection>
        <h3>{t('definition.metricDisplayStep')}</h3>
        <FormGrid>
          <Field>
            {t('definition.appearance')}
            <select
              value={metric.presentation.renderer}
              onChange={(event) =>
                onChange({
                  ...metric,
                  presentation: { ...metric.presentation, renderer: event.target.value as MetricRenderer },
                })
              }
            >
              {(calculation.type === 'scaleBands' || calculation.type === 'respondentSegment') && (
                <option value="distribution">{t('definition.distribution')}</option>
              )}
              <option value="bar">{t('definition.bar')}</option>
              <option value="score">{t('definition.score')}</option>
              {(composed || metric.presentation.renderer === 'breakdown') && (
                <option value="breakdown">{t('definition.breakdown')}</option>
              )}
            </select>
          </Field>
          <Field>
            {t('definition.format')}
            <select
              value={metric.presentation.format}
              onChange={(event) =>
                onChange({
                  ...metric,
                  presentation: {
                    ...metric.presentation,
                    format: event.target.value as ReportMetricDefinition['presentation']['format'],
                  },
                })
              }
            >
              {(['outOfMax', 'rangePercent', 'maxPercent', 'number'] as const)
                .filter((format) => calculation.type !== 'nps' || format === 'number')
                .filter(
                  (format) =>
                    (calculation.type !== 'scaleBands' && calculation.type !== 'respondentSegment') ||
                    ['maxPercent', 'number'].includes(format),
                )
                .map((format) => (
                  <option key={format} value={format}>
                    {t(`definition.formats.${format}`)}
                  </option>
                ))}
            </select>
          </Field>
          {onWidthChange && (
            <Field>
              {t('definition.width')}
              <select value={width ?? 'full'} onChange={(event) => onWidthChange(event.target.value as ComponentWidth)}>
                {(['full', 'half', 'third', 'quarter'] as const).map((value) => (
                  <option key={value} value={value}>
                    {t(`definition.widths.${value}`)}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </FormGrid>
        <Hint>{t('definition.percentHelp')}</Hint>
      </FormSection>
      <FormSection>
        <h3>{t('definition.preview')}</h3>
        {error ? (
          <ErrorText>{t('definition.invalid', { reason: t(`definition.validation.${error}`) })}</ErrorText>
        ) : (
          <>
            {metric.presentation.renderer !== 'bar' && summary?.scale && summary.value !== undefined ? (
              <PreviewTotalScore value={summary.value} max={summary.max} language={i18n.language} t={tKpiCard} />
            ) : (
              <MetricValue
                summary={summary}
                format={metric.presentation.format}
                bar={metric.presentation.renderer === 'bar'}
              />
            )}
            {metric.presentation.renderer === 'distribution' && summary?.bands && summary.value !== undefined && (
              <BandDistribution bands={summary.bands} count={summary.count} />
            )}
            {metric.presentation.renderer === 'breakdown' && summary?.parts?.length ? (
              <PreviewBreakdown>
                {summary.parts.map((part) => {
                  const label =
                    part.kind === 'metric'
                      ? localizedText(metrics.find((item) => item.id === part.metricId)?.title, i18n.language)
                      : (part.statementIndex !== undefined
                          ? questions.find((item) => item.index === part.questionIndex)?.statementsLocal?.[
                              part.statementIndex
                            ]
                          : undefined) ?? questions.find((item) => item.index === part.questionIndex)?.text;
                  const evaluation =
                    part.value === undefined || !part.scale
                      ? undefined
                      : evaluateKpi(part.value, KPI_METRIC_TYPE.SCALE, tKpiCard);
                  return (
                    <PreviewBreakdownRow key={part.id}>
                      <span>{label || t('definition.unavailable')}</span>
                      <PreviewBreakdownValue style={{ color: getScoreColor(evaluation?.state ?? 'normal') }}>
                        {evaluation && (
                          <Badge
                            text={evaluation.badgeText}
                            color={badgeStateToColor(evaluation.state)}
                            subtle
                            size="Small"
                          />
                        )}
                        {part.value === undefined
                          ? '—'
                          : formatMetricNumber(part.value, i18n.language, part.scale ? 'scale' : undefined)}
                      </PreviewBreakdownValue>
                    </PreviewBreakdownRow>
                  );
                })}
              </PreviewBreakdown>
            ) : null}

            <Hint>{t(summary?.value === undefined ? 'definition.metricNoData' : 'definition.metricPreviewHelp')}</Hint>
          </>
        )}
      </FormSection>
    </div>
  );
}

const PreviewBreakdown = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 ${theme.spacing.g};
  margin-top: ${theme.spacing.e};
`;

const PreviewBreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.spacing.d};
  padding: ${theme.spacing.e} 0;
  border-top: 1px solid ${theme.colors.borderSeparator.default};
`;

const PreviewBreakdownValue = styled.strong`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
`;

function PreviewTotalScore({
  value,
  max,
  language,
  t,
}: {
  value: number;
  max: number;
  language: string;
  t: TFunction;
}) {
  const evaluation = evaluateKpi(value, KPI_METRIC_TYPE.SCALE, t);
  return (
    <PreviewTotalScoreContainer>
      <PreviewTotalScoreValue style={{ color: getScoreColor(evaluation.state) }}>
        {formatMetricNumber(value, language, 'scale')}
        <PreviewTotalScoreMax>/ {max}</PreviewTotalScoreMax>
      </PreviewTotalScoreValue>
      <Badge text={evaluation.badgeText} color={badgeStateToColor(evaluation.state)} subtle size="Small" />
    </PreviewTotalScoreContainer>
  );
}

const PreviewTotalScoreContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;
`;

const PreviewTotalScoreValue = styled.strong`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.c};
`;

const PreviewTotalScoreMax = styled.span`
  color: ${theme.colors.text.secondary};
  font-weight: normal;
`;
